/* eslint-disable no-param-reassign */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { SongType } from 'services/api/entities';
import { remixerApi } from 'services/api/remixer';
import { NAMESPACE } from './constants';
import fetchSong from './fetchSong';
import initialState from './initialState';
import { Effects } from './types';

const remixerSlice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    deleteSongRemix: (state) => {
      const currentSongId =
        state.project.history[state.project.historyPointer]?.songRequestId;
      if (!currentSongId) {
        return;
      }

      delete state.songs[currentSongId];
      state.project.history.splice(state.project.historyPointer, 1);
      state.project.historyPointer -= 1;
    },
    undo: (state) => {
      if (state.project.historyPointer === -1) {
        return;
      }
      state.project.historyPointer -= 1;
    },
    redo: (state) => {
      if (state.project.historyPointer === state.project.history.length - 1) {
        return;
      }
      state.project.historyPointer += 1;
    },
    setEffectValue: (
      state,
      action: PayloadAction<{ effect: Effects; value: number }>
    ) => {
      const {
        payload: { effect, value },
      } = action;
      state.project.effects[effect] = value;
    },
    setTitle: (state, action: PayloadAction<string>) => {
      state.project.title = action.payload;
    },
    clearState: (state) => {
      state.project.history = initialState.project.history;
      state.project.historyPointer = initialState.project.historyPointer;
      state.songs = initialState.songs;
      state.project.effects = initialState.project.effects;
      state.project.title = initialState.project.title;
      state.project.id = initialState.project.id;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSong.pending, (state, { meta: { requestId } }) => {
      state.initialSongRequestId = requestId;
      state.songs[requestId] = {
        data: null,
        loading: true,
        error: null,
      };
    });
    builder.addCase(
      fetchSong.fulfilled,
      (state, { payload: data, meta: { requestId } }) => {
        const { musicFilePath, duration, title, songType } = data;
        state.project.musicFilePath = musicFilePath;
        state.project.duration = duration;
        state.project.title = title;

        state.prevProjectState = state.project;

        const projectSongProp =
          songType === SongType.AI_GENERATED ? 'vegaSong' : 'song';
        state.project[projectSongProp] = data;

        state.songs[requestId] = {
          data,
          loading: false,
          error: null,
        };
      }
    );
    builder.addCase(
      fetchSong.rejected,
      (state, { error, meta: { requestId } }) => {
        state.songs[requestId] = {
          data: null,
          loading: false,
          error,
        };
      }
    );
    builder.addMatcher(
      remixerApi.endpoints.remixSong.matchPending,
      (state, action) => {
        const {
          meta: {
            requestId,
            arg: { originalArgs },
          },
        } = action;

        state.songs[requestId] = {
          data: null,
          loading: true,
          error: null,
        };

        const songsToDelete = state.project.history.splice(
          state.project.historyPointer + 1
        );
        songsToDelete.forEach(
          ({ songRequestId }) => delete state.songs[songRequestId]
        );

        state.project.history.push({
          songRequestId: requestId,
          formula: originalArgs,
        });

        state.project.historyPointer += 1;
      }
    );
    builder.addMatcher(
      remixerApi.endpoints.remixSong.matchFulfilled,
      (state, { payload: data, meta: { requestId } }) => {
        state.songs[requestId] = {
          data,
          loading: false,
          error: null,
        };
      }
    );
    builder.addMatcher(
      remixerApi.endpoints.remixSong.matchRejected,
      (state, { error, meta: { requestId } }) => {
        state.songs[requestId] = {
          data: null,
          loading: false,
          error,
        };
      }
    );
    builder.addMatcher(
      remixerApi.endpoints.saveProject.matchFulfilled,
      (state, { payload: data }) => {
        const { id, lastSavedAt, createdAt } = data;
        state.project.id = id;
        state.project.lastSavedAt = lastSavedAt;
        state.project.createdAt = createdAt;

        state.prevProjectState = state.project;
      }
    );

    builder.addMatcher(
      remixerApi.endpoints.getProject.matchPending,
      (state, { meta: { requestId } }) => {
        state.initialSongRequestId = requestId;
        state.songs[requestId] = {
          data: null,
          loading: true,
          error: null,
        };
      }
    );
    builder.addMatcher(
      remixerApi.endpoints.getProject.matchRejected,
      (state, { error, meta: { requestId } }) => {
        state.songs[requestId] = {
          data: null,
          loading: false,
          error,
        };
      }
    );
    builder.addMatcher(
      remixerApi.endpoints.getProject.matchFulfilled,
      (state, { payload: data, meta: { requestId } }) => {
        const song = data.song || data.vegaSong;

        if (song) {
          state.songs[requestId] = {
            data: song,
            loading: false,
            error: null,
          };
        } else {
          state.songs[requestId] = {
            data: null,
            loading: false,
            error: new Error('could not find project song'),
          };
        }

        const { historySongs, ...projectData } = data;

        state.project = projectData;

        state.prevProjectState = state.project;

        historySongs.forEach((historySong) => {
          state.songs[historySong.id] = {
            data: historySong,
            loading: false,
            error: null,
          };
        });
      }
    );
  },
});

export const { actions, reducer } = remixerSlice;
